/**
 * The web device reading its own inbox (ADR-0027 D4.6, D4.8 step 5).
 *
 * No new mechanism, and none was invented. `fetch_encrypted_inbox` and
 * `ack_encrypted` have existed on the chat channel since the phones shipped;
 * this browser is a device like any other and pulls the envelopes addressed to
 * IT. A message sent from a phone is encrypted to this browser at send time
 * like any other destination, so it arrives in the same push that carries it
 * to the recipient — no phone relay, and therefore no phone-shaped latency.
 *
 * ## The order is the design
 *
 *   fetch → decrypt → **persist** → ack
 *
 * The ack DELETES the ciphertext server-side. Persisting after it would open a
 * window in which the only copy of a message exists in a promise. So the write
 * lands first and the ack carries only what is already durable — which is why
 * `onMessage` is awaited and its failure keeps the envelope unacked. This is
 * the same order `ChatService.persistDecrypted` runs in on the phones, for the
 * same reason.
 *
 * ## Three outcomes, and the middle one is why they are three
 *
 *   * **ok** — persisted, then acked.
 *   * **held** — the peer's identity key changed. RECOVERABLE by a person, so
 *     the envelope stays in the inbox: not acked, not counted against its
 *     attempts, and readable by a later drain once the change is accepted.
 *     Acking it would delete a message on the user's behalf because their
 *     friend reinstalled.
 *   * **failed** — dead for this envelope. Given a bounded few drains as a
 *     transient guard, then surfaced as a "couldn't read this" tombstone and
 *     acked, so it neither re-fetches forever nor vanishes with no trace. An
 *     envelope that silently disappears is the worst of the three, because
 *     nobody can even report it.
 *
 * ## Serialised
 *
 * One drain at a time. The doorbell and the catch-up both fire on a reconnect,
 * and two concurrent drains would decrypt the same envelope twice — against a
 * ratchet that only moves forward, so the second read fails and a good message
 * gets counted as dead.
 */
import type { MessageCache, CachedMessage } from "./message-cache.ts";
import type { DecryptOutcome } from "./protocol.ts";
import { decodeContent } from "./e2e-content.ts";

/** The one thing the drain asks of the worker. Narrower than `SignalClient` on
 *  purpose — this module must not be able to encrypt, publish or destroy, and
 *  an interface is how that stays checkable rather than merely true today. */
export interface Decrypter {
  decrypt(
    peerName: string,
    peerDevice: number,
    type: number,
    ciphertext: Uint8Array,
  ): Promise<DecryptOutcome>;
}

/** A ciphertext envelope as the server hands it over — the `fetch_encrypted_inbox`
 *  reply shape, field-for-field. */
export interface EncryptedEnvelope {
  id?: string | null;
  chat_id?: string | null;
  sender_user_id?: string | null;
  /** The sender's Signal device NUMBER, joined server-side. This — not the
   *  device-row UUID beside it — is half of the libsignal address we decrypt
   *  under. Null when the sender's device has since been revoked. */
  sender_device_no?: number | null;
  message_type?: string | null;
  /** base64 */
  ciphertext?: string | null;
  client_msg_id?: string | null;
  created_at?: string | null;
}

/**
 * libsignal's ciphertext type discriminants, by the server's wire name.
 *
 * The 1:1 pair ONLY. The server's vocabulary also has `sender-key` and
 * `sender-key-distribution` — the group path — and those are deliberately
 * absent: Sender Keys run in this browser's engine but nothing on this surface
 * routes them, so mapping the name would claim a capability that does not
 * exist and turn "we cannot read group messages yet" into three failed decrypt
 * attempts that say the same thing more slowly. An unmapped name is not a guess
 * to make; it is an envelope this build cannot read, and it says so at once.
 */
const WIRE_TYPE: Record<string, number> = {
  "pre-key": 3,
  signal: 2,
};

/** How many drains a dead envelope gets before it becomes a tombstone. Low
 *  enough that a genuinely broken message resolves within one session; more
 *  than one so a transient failure (a worker restarting mid-drain) is not
 *  instantly permanent. */
const MAX_ATTEMPTS = 3;

/** How many inbox pages one drain will walk before handing back. A ceiling
 *  rather than a limit anybody should hit: it stops a pathological inbox from
 *  holding one call open indefinitely, and whatever is left is read by the
 *  next drain. */
const MAX_PAGES = 50;

/** What the channel gives this module: the two pushes, and nothing else. It
 *  is deliberately not a `Channel` — the drain has no business joining,
 *  leaving, or listening to anything. */
export interface InboxTransport {
  fetch(deviceRowId: string): Promise<EncryptedEnvelope[]>;
  ack(deviceRowId: string, messageIds: string[]): Promise<void>;
}

export interface InboxDeps {
  signal: Decrypter;
  cache: MessageCache;
  transport: InboxTransport;
  /** This device's server row id — what the inbox is scoped to. Read per drain
   *  rather than captured: a browser that links mid-session becomes a device
   *  without reloading, and a captured null would keep it deaf until it did. */
  deviceRowId: () => Promise<string | null>;
  /** Called once per message that is now readable, AFTER it is durable. Its
   *  rejection keeps the envelope unacked. */
  onMessage: (message: CachedMessage) => void;
  /**
   * ADR-0027 D4.7 — how far through a history bundle we are, reported as each
   * frame arrives.
   *
   * [seq] and [total] come off the FRAME, not from counting here. A frame that
   * is redelivered repeats a number already seen and cannot move the bar
   * backwards or double-count, which counting locally would do the moment an
   * unacked envelope came round again.
   */
  onSyncProgress?: (seq: number, total: number) => void;
}

export class EncryptedInbox {
  private readonly deps: InboxDeps;
  /** client_msg_id → consecutive failed decrypts. */
  private readonly attempts = new Map<string, number>();
  private running: Promise<number> | null = null;

  constructor(deps: InboxDeps) {
    this.deps = deps;
  }

  /**
   * Drain once. Resolves with how many messages became readable.
   *
   * Concurrent callers JOIN the drain in flight rather than starting a second
   * one — the doorbell and the reconnect catch-up fire together often enough
   * that this is the ordinary case, not the race.
   */
  drain(): Promise<number> {
    if (this.running) return this.running;
    this.running = this.run().finally(() => {
      this.running = null;
    });
    return this.running;
  }

  private async run(): Promise<number> {
    const deviceRowId = await this.deps.deviceRowId();
    // Not a device yet. Not an error either: a browser that has not been
    // approved by a phone has an inbox nobody can address.
    if (!deviceRowId) return 0;

    let read = 0;
    // The server answers a PAGE of the inbox, not the whole of it (200
    // envelopes), and a history bundle (ADR-0027 D4.7) is thousands. A single
    // fetch would read the first page and leave the rest sitting there until
    // something else happened to ring the doorbell — which, for a bundle
    // covering chats nobody has open, might be never.
    for (let page = 0; page < MAX_PAGES; page++) {
      const envelopes = await this.deps.transport.fetch(deviceRowId);
      if (envelopes.length === 0) return read;

      const ack: string[] = [];
      for (const env of envelopes) {
        const outcome = await this.readOne(env);
        if (outcome === "read") {
          read++;
          if (env.id) ack.push(env.id);
        } else if (outcome === "tombstoned") {
          if (env.id) ack.push(env.id);
        }
        // "held" and "kept" leave the envelope where it is, deliberately.
      }

      // Nothing was acked, so the next fetch returns the SAME page. That is
      // the termination condition, and it is the honest one: a page entirely
      // of held messages is a page this drain cannot get past, and looping on
      // it would spin forever rather than wait for the person to accept an
      // identity change.
      if (ack.length === 0) return read;
      await this.deps.transport.ack(deviceRowId, ack);
    }
    return read;
  }

  private async readOne(env: EncryptedEnvelope): Promise<"read" | "held" | "kept" | "tombstoned"> {
    const chatId = env.chat_id;
    const senderUserId = env.sender_user_id;
    const clientMsgId = env.client_msg_id;
    const type = env.message_type ? WIRE_TYPE[env.message_type] : undefined;

    // An envelope missing any of these cannot be addressed, decrypted or
    // stored. It is dropped as a tombstone rather than kept: re-fetching it
    // forever would be the same result plus a growing backlog, and the
    // remaining fields are not enough to render anything a person could act on.
    if (!chatId || !senderUserId || !clientMsgId || !env.ciphertext || env.sender_device_no == null || type === undefined) {
      return "tombstoned";
    }

    const outcome = await this.deps.signal.decrypt(
      senderUserId,
      env.sender_device_no,
      type,
      bytes(env.ciphertext),
    );

    if (outcome.outcome === "held") return "held";

    if (outcome.outcome === "failed") {
      const n = (this.attempts.get(clientMsgId) ?? 0) + 1;
      if (n < MAX_ATTEMPTS) {
        this.attempts.set(clientMsgId, n);
        return "kept";
      }
      this.attempts.delete(clientMsgId);
      await this.deliver({
        clientMsgId,
        chatId,
        senderUserId,
        body: "",
        createdAt: env.created_at ?? new Date().toISOString(),
        undecryptable: true,
      });
      return "tombstoned";
    }

    this.attempts.delete(clientMsgId);
    const content = decodeContent(outcome.plaintext);

    // ADR-0027 D4.7 — a history frame is not news, it is a copy, and almost
    // nothing about the envelope describes it. The author and the time come
    // out of the FRAME; the envelope's `sender_user_id` is the phone that did
    // the syncing and its `created_at` is a minute ago. Reading those would
    // file six months of conversation under today's date, all of it apparently
    // said by the person who linked the browser.
    const history = content.history;
    if (history) {
      await this.deliver({
        clientMsgId,
        chatId,
        senderUserId: history.from,
        body: content.body,
        createdAt: history.at,
        type: history.type,
        mediaUrl: history.mediaUrl,
        thumbUrl: history.thumbUrl,
        replyToId: content.replyToId,
        forwarded: content.forwarded,
        stickerRef: content.stickerRef,
        // Already absolute. A11's deadline was computed when the message was
        // first sent and is re-sent as a wall clock, never as a fresh duration.
        expiresAt: history.expiresAt,
      });
      this.deps.onSyncProgress?.(history.seq, history.total);
      return "read";
    }

    const createdAt = env.created_at ?? new Date().toISOString();
    await this.deliver({
      clientMsgId,
      chatId,
      senderUserId,
      body: content.body,
      createdAt,
      replyToId: content.replyToId,
      forwarded: content.forwarded,
      stickerRef: content.stickerRef,
      // A11 — from when the SENDER sent it, not from when this browser
      // happened to decrypt it. A reader who was away for two days does not
      // get a fresh timer.
      expiresAt:
        content.expireSeconds == null
          ? null
          : Date.parse(createdAt) + content.expireSeconds * 1000,
    });
    return "read";
  }

  /** Durable first, on screen second. If the write throws, nothing is acked
   *  and the envelope comes back on the next drain — which is the whole point
   *  of doing it in this order. */
  private async deliver(message: CachedMessage): Promise<void> {
    await this.deps.cache.put(message);
    this.deps.onMessage(message);
  }
}

function bytes(b64: string): Uint8Array {
  const raw = atob(b64);
  const out = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i);
  return out;
}
